import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award, Bookmark, Check, ChevronDown, Clock3, Cloud, Coffee, CupSoda, Globe2, List,
  LocateFixed, LogOut, Map as MapIcon, MapPin, Search, Settings, SlidersHorizontal,
  MessageCircle, Sparkles, Star, Trash2, Users, Utensils, X
} from 'lucide-react';
import DishPhotoSearch from './components/DishPhotoSearch';
import AIChatModal from './components/AIChatModal';
import GroupVote from './components/GroupVote';
import LoginModal from './components/LoginModal';
import MapComponent from './components/MapComponent';
import PlaceDetailModal from './components/PlaceDetailModal';
import RecommendationList from './components/RecommendationList';
import RegisterModal from './components/RegisterModal';
import { useAuth } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import aiRecommendationService from './services/aiRecommendationService';
import favoritesService from './services/favoritesService';
import googleMapsService from './services/googleMapsService';
import groupVoteService, { VOTE_ID_PATTERN } from './services/groupVoteService';
import visitFeedbackService from './services/visitFeedbackService';
import { deadlineToTimestamp } from './utils/schedulePlanner';

const UI = {
  'zh-TW': {
    promise: '60 秒，選好這一餐', explore: '探索', saved: '收藏', now: '現在想吃什麼？',
    placeholder: '拉麵、咖啡、牛肉麵…', location: '探索位置', locate: '使用目前位置',
    manual: '輸入地區或地標', find: '開始找',
    top: '最適合現在的選擇', map: '地圖', list: '清單', filters: '偏好條件',
    openNow: '只看營業中', more: '查看更多', settings: '設定', sync: '同步收藏',
    recent: '最近搜尋', clear: '清除', searching: '正在找附近最適合的選擇…'
  },
  en: {
    promise: 'Pick this meal in 60 seconds', explore: 'Explore', saved: 'Saved', now: 'What do you feel like eating?',
    placeholder: 'Ramen, coffee, noodles…', location: 'Search location', locate: 'Use current location',
    manual: 'Enter an area or landmark', find: 'Find places',
    top: 'Best picks for right now', map: 'Map', list: 'List', filters: 'Preferences',
    openNow: 'Open now', more: 'See more', settings: 'Settings', sync: 'Sync saved places',
    recent: 'Recent searches', clear: 'Clear', searching: 'Finding the best nearby choices…'
  }
};

const CATEGORY_VALUES = [
  { value: 'restaurant', zh: '餐廳', en: 'Restaurant', icon: Utensils },
  { value: 'cafe', zh: '咖啡', en: 'Coffee', icon: Coffee },
  { value: 'bubble-tea', type: 'cafe', zh: '手搖飲', en: 'Bubble tea', queryZh: '手搖飲店', queryEn: 'bubble tea shop', icon: CupSoda },
  { value: 'bakery', zh: '甜點', en: 'Dessert', icon: Sparkles },
  { value: 'meal_takeaway', zh: '外帶', en: 'Takeaway', icon: Clock3 },
  { value: 'bib-gourmand', type: 'restaurant', zh: '必比登', en: 'Bib Gourmand', queryZh: '米其林必比登推介', queryEn: 'Michelin Bib Gourmand', icon: Award },
  { value: 'michelin-guide', type: 'restaurant', zh: '米其林指南', en: 'MICHELIN Guide', queryZh: '米其林指南推薦餐廳', queryEn: 'MICHELIN Guide restaurant', icon: Star }
];

const readRecent = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('chi_na_jian_recent') || '[]');
    return Array.isArray(stored) ? stored.filter(item => typeof item === 'string' && item.trim() && !['直接幫我選', 'Pick for me'].includes(item)).slice(0, 5) : [];
  }
  catch { return []; }
};

const readVoteId = () => {
  const id = new URLSearchParams(window.location.search).get('vote');
  return VOTE_ID_PATTERN.test(id || '') ? id : null;
};

const defaultDeadline = () => {
  const date = new Date(Date.now() + 90 * 60000);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

function App() {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const { currentLanguage, changeLanguage } = useLanguage();
  const text = UI[currentLanguage] || UI['zh-TW'];
  const chinese = currentLanguage === 'zh-TW';
  const manualInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('explore');
  const [voteId, setVoteId] = useState(readVoteId);
  const [voteBusy, setVoteBusy] = useState(false);
  const [visitPrompt, setVisitPrompt] = useState(() => visitFeedbackService.getPrompt());
  const [visitOutcomes, setVisitOutcomes] = useState(() => visitFeedbackService.getOutcomes());
  const [intent, setIntent] = useState('');
  const [category, setCategory] = useState('restaurant');
  const [location, setLocation] = useState(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [manualLocation, setManualLocation] = useState('');
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [recent, setRecent] = useState(readRecent);
  const [excludeIds, setExcludeIds] = useState([]);
  const [resultTitle, setResultTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [rawPlaces, setRawPlaces] = useState([]);
  const [assistantPickPending, setAssistantPickPending] = useState(false);
  const searchVersion = useRef(0);
  const currentLocation = useRef(location);
  currentLocation.current = location;
  const [searchStarted, setSearchStarted] = useState(false);
  const [reviewCoverage, setReviewCoverage] = useState(null);
  const reviewVersion = useRef(0);
  const [walkMinutes, setWalkMinutes] = useState(15);
  const [priceRange, setPriceRange] = useState('all');
  const [openNow, setOpenNow] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [deadline, setDeadline] = useState(defaultDeadline);
  const [mealMinutes, setMealMinutes] = useState(45);
  const [nextStop, setNextStop] = useState('');
  const [schedulePlan, setSchedulePlan] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [isLoading, setIsLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [favorites, setFavorites] = useState(() => favoritesService.getFavorites());
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(() => localStorage.getItem('chi_na_jian_sync_enabled') === 'true');

  const categories = useMemo(() => CATEGORY_VALUES.map(item => ({
    ...item,
    label: chinese ? item.zh : item.en,
    query: chinese ? (item.queryZh || item.zh) : (item.queryEn || item.en)
  })), [chinese]);
  const guideSelected = category === 'bib-gourmand' || category === 'michelin-guide';
  const priceOptions = chinese ? [
    ['all', '不限', '任何預算'], ['low', '平價', 'NT$300 以下'],
    ['medium', '中等', 'NT$300–800'], ['high', '高價', 'NT$800 以上']
  ] : [
    ['all', 'Any', 'Any budget'], ['low', 'Budget', 'Under NT$300'],
    ['medium', 'Moderate', 'NT$300–800'], ['high', 'Premium', 'NT$800+']
  ];
  const selectedPrice = priceOptions.find(([value]) => value === priceRange)?.[2];

  const track = event => window.gtag?.('event', event);

  const syncFavorites = useCallback(async () => {
    if (!user) {
      localStorage.setItem('chi_na_jian_sync_pending', 'true');
      setShowSettings(false);
      setShowLogin(true);
      return;
    }
    setSyncBusy(true);
    try {
      const merged = await favoritesService.syncWithCloud(user.id);
      setFavorites(merged);
      setSyncEnabled(true);
      localStorage.setItem('chi_na_jian_sync_enabled', 'true');
      localStorage.removeItem('chi_na_jian_sync_pending');
      setStatus(chinese ? '收藏已同步' : 'Saved places synced');
    } catch {
      setError(chinese ? '同步失敗，本機收藏仍安全保留。' : 'Sync failed. Local saved places are still safe.');
    } finally {
      setSyncBusy(false);
    }
  }, [chinese, user]);

  useEffect(() => {
    if (user && (syncEnabled || localStorage.getItem('chi_na_jian_sync_pending') === 'true')) syncFavorites();
  }, [syncEnabled, syncFavorites, user]);

  const recommendations = useMemo(() => !searchStarted || !location ? [] : aiRecommendationService.getRecommendations(rawPlaces, location, {
    distance: walkMinutes * 80, priceRange, openNow, schedule: schedulePlan, excludeIds, favorites, visitOutcomes,
    ignoreRating: Boolean(reviewCoverage)
  }), [excludeIds, favorites, location, openNow, priceRange, rawPlaces, reviewCoverage, schedulePlan, searchStarted, visitOutcomes, walkMinutes]);

  useEffect(() => {
    if (!searchStarted || isLoading) return;
    setShowAll(Boolean(reviewCoverage));
    if (!error) setStatus(recommendations.length ? `${recommendations.length} ${chinese ? '間符合條件' : 'matches'}` : (chinese ? '目前沒有符合條件的餐廳' : 'No matching places'));
  }, [chinese, error, isLoading, recommendations, reviewCoverage, searchStarted]);

  useEffect(() => {
    if (!showSettings) return;
    const close = event => event.key === 'Escape' && setShowSettings(false);
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [showSettings]);

  useEffect(() => {
    reviewVersion.current++;
  }, [location, walkMinutes, priceRange, openNow, scheduleEnabled, deadline, mealMinutes, nextStop, intent, category]);

  useEffect(() => () => { searchVersion.current++; reviewVersion.current++; }, []);

  useEffect(() => {
    // People come back to the tab after eating; that is when to ask how it went.
    const check = () => document.visibilityState === 'visible' && setVisitPrompt(visitFeedbackService.getPrompt());
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);

  const answerVisit = async outcome => {
    setVisitPrompt(null);
    setVisitOutcomes(await visitFeedbackService.resolve(outcome));
    if (outcome !== 'skipped') setStatus(chinese ? '謝謝！下次推薦會參考這次的感想' : 'Thanks! Future picks will use this');
  };

  useEffect(() => {
    const sync = () => setVoteId(readVoteId());
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  const openVote = id => {
    window.history.pushState(null, '', `${window.location.pathname}?vote=${id}`);
    setVoteId(id);
    window.scrollTo({ top: 0 });
  };

  const leaveVote = () => {
    window.history.replaceState(null, '', window.location.pathname);
    setVoteId(null);
    setActiveTab('explore');
  };

  const startVote = async () => {
    if (voteBusy) return;
    setVoteBusy(true);
    setError('');
    try {
      const id = await groupVoteService.createVote(recommendations.slice(0, 3), resultTitle || (searchQuery ? (chinese ? `${searchQuery}，吃哪間？` : `${searchQuery}: where to?`) : ''));
      openVote(id);
      track('group_vote_created');
      const url = groupVoteService.shareUrl(id);
      try {
        if (navigator.share) await navigator.share({ title: chinese ? '今天吃哪間？' : 'Where should we eat?', text: chinese ? '幫我投一下，選好就出發！' : 'Vote so we can go!', url });
        else { await navigator.clipboard.writeText(url); setStatus(chinese ? '投票連結已複製，貼給朋友吧' : 'Vote link copied — send it to friends'); }
      } catch { /* The vote page has its own invite button. */ }
    } catch {
      setError(chinese ? '投票暫時無法建立。你仍可在店家詳情分享單一餐廳。' : 'Could not start a vote. You can still share a single place from its details.');
    } finally {
      setVoteBusy(false);
    }
  };

  const chooseCategory = item => {
    setCategory(item.value);
    setIntent(item.query);
  };

  const useCurrentLocation = async () => {
    setLocating(true);
    setError('');
    try {
      const current = await googleMapsService.getCurrentLocation();
      setLocation(current);
      setLocationLabel(chinese ? '目前位置' : 'Current location');
      setShowManualLocation(false);
      setStatus(chinese ? '已取得目前位置' : 'Current location ready');
    } catch (locationError) {
      setError(chinese ? locationError.message : 'Could not access your location. Allow location or enter a place.');
      setShowManualLocation(true);
      requestAnimationFrame(() => manualInputRef.current?.focus());
    } finally {
      setLocating(false);
    }
  };

  const resolveManualLocation = async event => {
    event.preventDefault();
    if (!manualLocation.trim()) return;
    setLocating(true);
    setError('');
    try {
      const result = await googleMapsService.geocodeAddress(manualLocation.trim());
      setLocation(result.location);
      setLocationLabel(result.label);
      setShowManualLocation(false);
      setStatus(chinese ? '探索位置已更新' : 'Search location updated');
    } catch (locationError) {
      setError(chinese ? locationError.message : 'Could not find that place. Check the Maps API key or try again.');
    } finally {
      setLocating(false);
    }
  };

  const rememberSearch = value => {
    if (!value) return;
    const next = [value, ...recent.filter(item => item !== value)].slice(0, 5);
    setRecent(next);
    localStorage.setItem('chi_na_jian_recent', JSON.stringify(next));
  };

  const search = async ({ direct = false, queryOverride, resultLabel = '', reviewBased = false } = {}) => {
    if (isLoading) return;
    if (!location) {
      setError(chinese ? '請先使用目前位置或輸入地點。' : 'Use your current location or enter a place first.');
      setShowManualLocation(true);
      return;
    }
    const version = ++reviewVersion.current;
    const generation = ++searchVersion.current;
    const isCurrent = () => generation === searchVersion.current && location === currentLocation.current;
    setReviewCoverage(null);
    setRawPlaces([]);
    setIsLoading(true);
    setError('');
    setSearchStarted(true);
    setStatus(text.searching);
    try {
      let nextPlan = null;
      if (scheduleEnabled) {
        const deadlineTimestamp = deadlineToTimestamp(deadline);
        if (!deadlineTimestamp) throw new Error(chinese ? '行程截止時間必須晚於現在。' : 'The deadline must be later than now.');
        const destination = nextStop.trim() ? (await googleMapsService.geocodeAddress(nextStop.trim())).location : location;
        nextPlan = { deadline: deadlineTimestamp, mealMinutes, destination };
      }
      if (!isCurrent()) return false;
      const query = queryOverride ?? intent.trim();
      setSearchQuery(direct ? '' : query);
      setExcludeIds([]);
      setResultTitle(reviewBased ? (chinese ? 'AI 看評論・不看星等' : 'AI review picks · No star ranking') : resultLabel);
      const placeType = CATEGORY_VALUES.find(item => item.value === category)?.type || category;
      const searchResult = direct || !query
        ? await googleMapsService.searchNearbyPlaces(location, direct ? 'restaurant' : placeType, reviewBased ? walkMinutes * 80 : 2400, reviewBased)
        : await googleMapsService.searchPlacesByText(`${query} ${chinese ? '餐廳' : 'food'}`, location, reviewBased ? walkMinutes * 80 : 2400, reviewBased);
      if (!isCurrent()) return false;
      const places = reviewBased ? searchResult.places : searchResult;
      if (reviewBased && version !== reviewVersion.current) throw new Error('搜尋條件已變更，請重新分析。');
      setSchedulePlan(nextPlan);
      if (reviewBased) {
        const coverage = await aiRecommendationService.discoverFromReviews(places, location, { distance: walkMinutes * 80, priceRange, openNow, schedule: nextPlan, favorites },
          (stage, done, total) => setStatus(chinese ? (stage === 'details' ? `正在取得 ${total} 間店家的評論…` : `AI 正在閱讀評論 ${done}／${total}…`) : `Reading reviews ${done}/${total}…`),
          () => isCurrent() && version === reviewVersion.current);
        if (!isCurrent()) return false;
        const { places: _places, ...counts } = coverage;
        setReviewCoverage({ ...counts, searchIncomplete: searchResult.searchIncomplete });
        setRawPlaces(coverage.places);
        setShowAll(true);
      } else setRawPlaces(places);
      if (!direct) rememberSearch(query);
      track('restaurant_search');
      if (direct) setOpenNow(true);
      if (resultLabel || reviewBased) requestAnimationFrame(() => document.getElementById('results-title')?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
      return true;
    } catch (searchError) {
      if (!isCurrent()) return false;
      setStatus('');
      setRawPlaces([]);
      setError(chinese ? (searchError.message || '搜尋暫時失敗，請再試一次。') : 'Search failed. Check the Maps API key or try again.');
      return false;
    } finally {
      if (generation === searchVersion.current) {
        setIsLoading(false);
        if (location !== currentLocation.current) {
          setStatus('');
          setError(chinese ? '探索位置已變更，請重新搜尋。' : 'Location changed. Search again.');
        }
      }
    }
  };

  const pickForChat = async () => {
    if (isLoading) return false;
    setActiveTab('explore');
    setRecent(current => current.filter(item => !['直接幫我選', 'Pick for me'].includes(item)));
    if (!location) { await search({ direct: true }); return false; }
    setAssistantPickPending(true);
    const success = await search({ direct: true, resultLabel: chinese ? '附近現在能去的選擇' : 'Nearby places open now' });
    if (!success) setAssistantPickPending(false);
    return success;
  };

  const rescue = async place => {
    if (isLoading) return;
    const origin = place.details?.geometry?.location || place.geometry?.location;
    if (!origin) { setError(chinese ? '店家位置不足，請先設定探索位置再搜尋。' : 'Place location unavailable. Set a search location first.'); return; }
    const generation = ++searchVersion.current;
    const isCurrent = () => generation === searchVersion.current && location === currentLocation.current;
    reviewVersion.current++; setReviewCoverage(null);
    setIsLoading(true); setError('');
    try {
      const places = searchQuery
        ? await googleMapsService.searchPlacesByText(`${searchQuery} 餐廳`, origin, walkMinutes * 80)
        : await googleMapsService.searchNearbyPlaces(origin, 'restaurant', walkMinutes * 80);
      if (!isCurrent()) return;
      setLocation(origin); setLocationLabel(place.name);
      setExcludeIds(current => [...current, place.place_id]);
      setOpenNow(true); setSchedulePlan(null); setScheduleEnabled(false);
      setRawPlaces(places); setSearchStarted(true); setShowDetail(false); setActiveTab('explore');
      setResultTitle(chinese ? `${place.name} 附近的替代選擇` : `Alternatives near ${place.name}`);
    } catch (failure) { if (isCurrent()) setError(failure.message); }
    finally { if (generation === searchVersion.current) setIsLoading(false); }
  };

  const exploreSaved = async () => {
    if (isLoading) return;
    if (!location) {
      setActiveTab('explore'); setShowManualLocation(true);
      setError(chinese ? '請先設定探索位置，再到收藏點「附近有開的收藏」。' : 'Set a search location, then try saved places again.');
      return;
    }
    const generation = ++searchVersion.current;
    const isCurrent = () => generation === searchVersion.current && location === currentLocation.current;
    reviewVersion.current++; setReviewCoverage(null);
    setIsLoading(true); setError('');
    try {
      const nearby = favorites.filter(place => {
        const distance = googleMapsService.calculateDistance(location, place.geometry?.location);
        return distance == null || distance <= walkMinutes * 80;
      });
      const result = await googleMapsService.refreshSavedPlaces(nearby, isCurrent);
      if (!isCurrent()) return;
      setRawPlaces(result.places); setOpenNow(true); setExcludeIds([]); setSchedulePlan(null); setScheduleEnabled(false); setSearchQuery('');
      setSearchStarted(true); setActiveTab('explore');
      setResultTitle(chinese ? '附近有開的收藏' : 'Saved places open nearby');
      if (result.failed) setError(chinese ? `${result.failed} 間收藏無法確認最新資訊，本次未列入；原收藏仍保留。` : `${result.failed} saved places could not be refreshed and were omitted. Your saved list is preserved.`);
    } catch (failure) { if (isCurrent()) setError(failure.message); }
    finally { if (generation === searchVersion.current) setIsLoading(false); }
  };

  const openPlace = async place => {
    setSelectedPlace(place);
    setShowDetail(true);
    try {
      const details = await googleMapsService.getPlaceDetails(place.place_id);
      setSelectedPlace(current => current?.place_id === place.place_id ? { ...current, details } : current);
    } catch {
      setStatus(chinese ? '部分店家資訊暫時無法載入' : 'Some place details are unavailable');
    }
  };

  const toggleFavorite = async place => {
    favoritesService.isFavorite(place.place_id) ? favoritesService.removeFavorite(place.place_id) : favoritesService.addFavorite(place);
    setFavorites(favoritesService.getFavorites());
    if (syncEnabled && user) {
      try { await favoritesService.saveToCloud(user.id); }
      catch { setError(chinese ? '雲端同步失敗，本機變更已保留。' : 'Cloud sync failed. Local changes were saved.'); }
    }
  };

  const navigate = place => {
    const raw = place.geometry?.location;
    const value = item => typeof item === 'function' ? item() : item;
    const destination = raw ? `${value(raw.lat)},${value(raw.lng)}` : encodeURIComponent(place.name);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${encodeURIComponent(place.place_id)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    const rank = recommendations.findIndex(item => item.place_id === place.place_id) + 1;
    visitFeedbackService.recordNavigation(place, !voteId && activeTab === 'explore' && rank > 0 ? rank : null);
    track('navigation_started');
  };

  const share = async place => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}&query_place_id=${encodeURIComponent(place.place_id)}`;
    try {
      if (navigator.share) await navigator.share({ title: place.name, text: chinese ? `一起去 ${place.name}？` : `Want to try ${place.name}?`, url });
      else {
        await navigator.clipboard.writeText(url);
        setStatus(chinese ? '連結已複製' : 'Link copied');
      }
    } catch (shareError) {
      if (shareError.name !== 'AbortError') setError(chinese ? '無法分享，請稍後再試。' : 'Could not share. Try again.');
    }
  };

  const clearRecent = () => {
    setRecent([]);
    localStorage.removeItem('chi_na_jian_recent');
  };

  const savedRecommendations = favorites.map((place, index) => ({ ...place, matchLabel: chinese ? '已收藏' : 'Saved', recommendationReasons: place.recommendationReasons || [] }));

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">{chinese ? '跳到主要內容' : 'Skip to content'}</a>
      <header className="app-header">
        <a className="brand" href="#main-content" onClick={() => { if (voteId) leaveVote(); setActiveTab('explore'); }} aria-label={chinese ? '吃哪間首頁' : 'Foodie Pick home'}>
          <img className="brand-mark" src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" aria-hidden="true" />
          <span><strong>{chinese ? '吃哪間' : 'Foodie Pick'}</strong><small>{text.promise}</small></span>
        </a>
        <nav className="desktop-nav" aria-label={chinese ? '主要導覽' : 'Primary navigation'}><button className={activeTab === 'explore' ? 'active' : ''} aria-pressed={activeTab === 'explore'} onClick={() => { if (voteId) leaveVote(); setActiveTab('explore'); }}><Search aria-hidden="true" />{text.explore}</button><button className={activeTab === 'saved' ? 'active' : ''} aria-pressed={activeTab === 'saved'} onClick={() => { if (voteId) leaveVote(); setActiveTab('saved'); }}><Bookmark aria-hidden="true" />{text.saved}{favorites.length > 0 && <em>{favorites.length}</em>}</button></nav>
        <button className="icon-button settings-button" onClick={() => setShowSettings(value => !value)} aria-expanded={showSettings} aria-haspopup="dialog" aria-label={text.settings}><Settings aria-hidden="true" /></button>
        {showSettings && <div className="settings-popover" role="dialog" aria-label={text.settings}>
          <div className="settings-heading"><strong>{text.settings}</strong><button className="icon-button" onClick={() => setShowSettings(false)} aria-label={chinese ? '關閉設定' : 'Close settings'}><X aria-hidden="true" /></button></div>
          <div className="settings-section"><span className="settings-label"><Globe2 aria-hidden="true" />{chinese ? '語言' : 'Language'}</span><div className="segmented-control"><button className={chinese ? 'active' : ''} onClick={() => changeLanguage('zh-TW')}>繁中</button><button className={!chinese ? 'active' : ''} onClick={() => changeLanguage('en')}>EN</button></div></div>
          <div className="settings-section">
            <span className="settings-label"><Cloud aria-hidden="true" />{text.sync}</span>
            {user ? <><small>{user.email}</small><button className="secondary-button full-width" onClick={syncFavorites} disabled={syncBusy}>{syncBusy ? (chinese ? '同步中…' : 'Syncing…') : text.sync}</button></> : <button className="secondary-button full-width" onClick={syncFavorites}>{authLoading ? (chinese ? '確認帳號中…' : 'Checking account…') : (chinese ? '登入並同步' : 'Sign in to sync')}</button>}
          </div>
          <button className="settings-row" onClick={clearRecent}><Trash2 aria-hidden="true" />{chinese ? '清除最近搜尋' : 'Clear recent searches'}</button>
          <button className="settings-row" onClick={() => { visitFeedbackService.clear(); setVisitOutcomes({}); setVisitPrompt(null); setStatus(chinese ? '用餐感想已清除' : 'Meal feedback cleared'); }}><Trash2 aria-hidden="true" />{chinese ? '清除用餐感想' : 'Clear meal feedback'}</button>
          {isAuthenticated && <button className="settings-row" onClick={() => { logout(); setShowSettings(false); }}><LogOut aria-hidden="true" />{chinese ? '登出' : 'Sign out'}</button>}
        </div>}
      </header>

      <main id="main-content" className="main-content">
        <div className={status ? 'feedback-banner' : 'live-region'} role="status" aria-live="polite">{status && <>{isLoading ? <span className="spinner" aria-hidden="true" /> : <Check aria-hidden="true" />}<span>{status}</span><button className="icon-button" onClick={() => setStatus('')} aria-label={chinese ? '關閉狀態訊息' : 'Dismiss status'}><X aria-hidden="true" /></button></>}</div>
        {visitPrompt && !voteId && <section className="visit-feedback" aria-labelledby="visit-feedback-title">
          <div><strong id="visit-feedback-title">{chinese ? `上次去了「${visitPrompt.name}」，覺得如何？` : `How was ${visitPrompt.name}?`}</strong><small>{chinese ? '只會在這台裝置調整推薦；匿名回傳評分區間，不含店家名稱與位置。' : 'Adjusts picks on this device. Sends only anonymous rating ranges, never the place or your location.'}</small></div>
          <div className="visit-feedback-actions"><button className="secondary-button" onClick={() => answerVisit('liked')}>{chinese ? '好吃' : 'Loved it'}</button><button className="secondary-button" onClick={() => answerVisit('meh')}>{chinese ? '普通' : 'It was OK'}</button><button className="text-button" onClick={() => answerVisit('skipped')}>{chinese ? '沒去成' : "Didn't go"}</button></div>
        </section>}
        {error && <div className="error-banner" role="alert"><span>{error}</span><button onClick={() => setError('')} aria-label={chinese ? '關閉錯誤訊息' : 'Dismiss error'}><X aria-hidden="true" /></button></div>}

        {voteId ? <GroupVote voteId={voteId} chinese={chinese} onNavigate={navigate} onExit={leaveVote} /> : activeTab === 'explore' ? <>
          <div className="explore-top">
            <section className="explore-hero">
              <div className="hero-copy">
              <p className="eyebrow"><Sparkles aria-hidden="true" />{text.promise}</p>
              <h1>{chinese ? <>{'今天，吃點'}<br /><span>喜歡的。</span></> : <>Your next meal,<br /><span>made simple.</span></>}</h1>
              <p className="hero-description">{chinese ? '不用再滑半小時。從附近開始，找到現在就能出發的那一間。' : 'Less scrolling, more good food. Find a nearby place you can head to now.'}</p>
              <div className="hero-proof" aria-label={chinese ? '服務特色' : 'Highlights'}>
                <span><Check aria-hidden="true" />{chinese ? '免登入' : 'No sign-in'}</span>
                <span><Clock3 aria-hidden="true" />{chinese ? '即時營業' : 'Live hours'}</span>
                <span><MapPin aria-hidden="true" />{chinese ? '附近首選' : 'Nearby picks'}</span>
              </div>
              </div>
              <img className="hero-meal-art" src={`${process.env.PUBLIC_URL}/brand/home-hero-orange.webp`} width="800" height="800" alt="" aria-hidden="true" fetchPriority="high" />
            </section>

            <section className="search-card" aria-labelledby="search-heading">
            <div className="search-card-heading"><div><span className="section-kicker">{chinese ? '從這裡開始' : 'START HERE'}</span><h2 id="search-heading">{chinese ? '找到今天這一餐' : 'Find your next meal'}</h2></div></div>
            <div className="location-row">
              <div className={`location-status ${location ? 'is-ready' : ''}`}><span><MapPin aria-hidden="true" /></span><div><small>{text.location}</small><strong>{locationLabel || (chinese ? '尚未設定' : 'Not set')}</strong></div></div>
              <div className="location-actions"><button className="secondary-button" onClick={useCurrentLocation} disabled={locating}><LocateFixed aria-hidden="true" />{locating ? (chinese ? '定位中…' : 'Locating…') : text.locate}</button><button className="text-button" onClick={() => { setShowManualLocation(true); requestAnimationFrame(() => manualInputRef.current?.focus()); }}>{text.manual}</button></div>
            </div>
            {showManualLocation && <form className="manual-location-form" onSubmit={resolveManualLocation}><label htmlFor="manual-location">{text.manual}</label><div><input ref={manualInputRef} id="manual-location" value={manualLocation} onChange={event => setManualLocation(event.target.value)} placeholder={chinese ? '例如：台北車站、信義區' : 'Taipei Main Station'} /><button className="primary-button" type="submit" disabled={locating || !manualLocation.trim()}>{chinese ? '使用此地點' : 'Use this place'}</button></div></form>}

            <form className="intent-form" onSubmit={event => { event.preventDefault(); search(); }}>
              <label htmlFor="food-intent">{text.now}</label>
              <div className="search-input-wrap"><Search aria-hidden="true" /><input id="food-intent" value={intent} onChange={event => setIntent(event.target.value)} placeholder={text.placeholder} autoComplete="off" /><button type="submit" className="primary-button" disabled={isLoading}>{isLoading ? (chinese ? '搜尋中…' : 'Searching…') : text.find}</button></div>
              <div className="category-row">{categories.map(item => { const Icon = item.icon; return <button type="button" key={item.value} className={category === item.value && intent === item.query ? 'active' : ''} aria-pressed={category === item.value && intent === item.query} onClick={() => chooseCategory(item)}><Icon aria-hidden="true" />{item.label}</button>; })}</div>
              {guideSelected && <p className="guide-note">{chinese ? '依目前地點搜尋指南相關餐廳；入選狀態請以' : 'Searches guide-related restaurants near this location. Verify selection on the'} <a href="https://guide.michelin.com/tw/zh_TW" target="_blank" rel="noreferrer">{chinese ? '米其林指南官網' : 'official MICHELIN Guide'}</a>{chinese ? '為準。' : '.'}</p>}
              {!!recent.length && <div className="recent-row"><span>{text.recent}</span>{recent.map(item => <button type="button" key={item} onClick={() => setIntent(item)}>{item}</button>)}<button type="button" className="clear-recent" onClick={clearRecent}>{text.clear}</button></div>}
              <div className="search-extras"><section className={`schedule-planner ${scheduleEnabled ? 'is-active' : ''}`} aria-labelledby="schedule-title">
                <button type="button" className="schedule-toggle" onClick={() => setScheduleEnabled(value => !value)} aria-expanded={scheduleEnabled}><span><Clock3 aria-hidden="true" /><span><strong id="schedule-title">{chinese ? '準時吃得到' : 'Make it on time'}</strong><small>{chinese ? '把步行、用餐與下一站一起算進去' : 'Plan walking, dining and your next stop'}</small></span></span><span className="schedule-switch" aria-hidden="true" /></button>
                {scheduleEnabled && <div className="schedule-fields">
                  <label><span>{chinese ? '最晚幾點抵達下一站？' : 'Arrive by'}</span><input type="time" value={deadline} onChange={event => setDeadline(event.target.value)} required /></label>
                  <fieldset><legend>{chinese ? '預計用餐時間' : 'Meal time'}</legend><div>{[30, 45, 60].map(value => <button type="button" key={value} className={mealMinutes === value ? 'active' : ''} aria-pressed={mealMinutes === value} onClick={() => setMealMinutes(value)}>{value} {chinese ? '分鐘' : 'min'}</button>)}</div></fieldset>
                  <label><span>{chinese ? '下一站（選填）' : 'Next stop (optional)'}</span><input value={nextStop} onChange={event => setNextStop(event.target.value)} placeholder={chinese ? '例如：台北車站；未填則回到探索位置' : 'e.g. Taipei Main Station; blank returns here'} /></label>
                  <p>{chinese ? '包含 10 分鐘安全緩衝；步行時間為距離估算。' : 'Includes a 10-minute buffer; walking time is estimated.'}</p>
                </div>}
              </section>
              </div>
            </form>
            <div className="review-search-entry"><button type="button" className="secondary-button" disabled={isLoading} onClick={() => search({ reviewBased: true })}><MessageCircle aria-hidden="true" />{chinese ? '不看星等，AI 看評論' : 'Ignore stars · Read reviews with AI'}</button><p>{chinese ? '依現在的搜尋與範圍，閱讀 Google 提供的評論；評論文字會傳送至 AI 分析。' : 'Uses your current search and range. Google review text is sent to AI for analysis.'}</p></div>
            <DishPhotoSearch onRequestLocation={() => { setShowManualLocation(true); requestAnimationFrame(() => manualInputRef.current?.focus()); }} searching={isLoading} hasLocation={Boolean(location)} chinese={chinese} onSearch={dish => { setIntent(dish); setCategory('restaurant'); return search({ queryOverride: dish, resultLabel: chinese ? `照片辨識：${dish}・附近店家` : `Photo: ${dish} · Nearby places` }); }} />
            </section>
          </div>

          {searchStarted && <section className="results-section" aria-labelledby="results-title">
            <div className="results-heading"><div><p className="results-count">{recommendations.length ? `${recommendations.length} ${chinese ? '間符合條件' : 'matches'}` : (chinese ? '調整條件再試試' : 'Try different preferences')}</p><h2 id="results-title">{resultTitle || text.top}</h2></div><div className="view-toggle" aria-label={chinese ? '結果顯示方式' : 'Result view'}><button className={viewMode === 'list' ? 'active' : ''} aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}><List aria-hidden="true" />{text.list}</button><button className={viewMode === 'map' ? 'active' : ''} aria-pressed={viewMode === 'map'} onClick={() => setViewMode('map')}><MapIcon aria-hidden="true" />{text.map}</button></div></div>

            <details className="filter-panel">
              <summary>
                <span className="filter-icon"><SlidersHorizontal aria-hidden="true" /></span>
                <span className="filter-summary-copy"><strong>{text.filters}</strong><small>{walkMinutes} {chinese ? '分鐘內' : 'min'} · {selectedPrice}{openNow ? ` · ${text.openNow}` : ''}</small></span>
                <ChevronDown aria-hidden="true" />
              </summary>
              <div className="filter-content">
                <label className="check-control"><input type="checkbox" checked={openNow} onChange={event => setOpenNow(event.target.checked)} /><span><Check aria-hidden="true" /></span><span><strong>{text.openNow}</strong><small>{chinese ? '排除目前休息的店家' : 'Hide places that are closed'}</small></span></label>
                <fieldset><legend>{chinese ? '最多走多久？' : 'How far would you walk?'}</legend><div className="choice-row walk-row">{[5, 10, 15, 30].map(value => <button type="button" key={value} className={walkMinutes === value ? 'active' : ''} aria-pressed={walkMinutes === value} onClick={() => setWalkMinutes(value)}>{value}<small>{chinese ? '分鐘' : 'min'}</small></button>)}</div></fieldset>
                <fieldset><legend>{chinese ? '每人預算' : 'Budget per person'} <small>{chinese ? '依店家價位資訊估算' : 'estimated from place pricing'}</small></legend><div className="choice-row budget-row">{priceOptions.map(([value, label, range]) => <button type="button" key={value} className={priceRange === value ? 'active' : ''} aria-pressed={priceRange === value} onClick={() => setPriceRange(value)}><span>{label}</span><small>{range}</small></button>)}</div></fieldset>
              </div>
            </details>

            {reviewCoverage && <div className="review-coverage" role="status"><p>{chinese ? `AI 已分析 ${reviewCoverage.analyzed} 間，找到 ${recommendations.length} 間有餐點好評依據的店家。${reviewCoverage.searchIncomplete ? ' Google 搜尋未完整，僅分析已取得的店家。' : ''}` : `AI analyzed ${reviewCoverage.analyzed} places and found ${recommendations.length} supported picks.${reviewCoverage.searchIncomplete ? ' Search incomplete; only retrieved places were analyzed.' : ''}`}</p><details><summary>{chinese ? '查看分析範圍' : 'Analysis coverage'}</summary><p>{chinese ? `取得 ${reviewCoverage.searched} 間候選，${reviewCoverage.eligible} 間符合條件；${reviewCoverage.mixed} 間意見分歧、${reviewCoverage.insufficient} 間評論不足、${reviewCoverage.failed} 間查詢或分析失敗。Google 每次搜尋最多提供 60 間，每店最多 5 則評論，AI 閱讀每則前 300 字；不是範圍內所有店家的完整評價。未列入不代表不好吃。調整搜尋或放寬條件後，請重新分析。` : `${reviewCoverage.searched} candidates, ${reviewCoverage.eligible} eligible; ${reviewCoverage.mixed} mixed, ${reviewCoverage.insufficient} insufficient, ${reviewCoverage.failed} failed. Google returns up to 60 places and 5 reviews per place (first 300 characters analyzed). This is not complete coverage; omitted places are not necessarily bad. Run again after changing your search or filters.`}</p></details></div>}

            {isLoading ? <div className="loading-state" role="status"><span className="spinner" />{status || text.searching}</div> : <div className="results-grid"><div className={`results-list ${viewMode === 'map' ? 'mobile-hidden' : ''}`}><div className="assistant-callout"><div><Sparkles aria-hidden="true" /><span><strong>{chinese ? '還是選不出來？' : 'Still stuck?'}</strong><small>{chinese ? '只比較目前候選，不使用位置或歷史側寫。' : 'Compares current candidates without location or profiling.'}</small></span></div><button className="secondary-button" onClick={() => setShowAssistant(true)} disabled={!recommendations.length}>{chinese ? '幫我選' : 'Pick for me'}</button></div>{recommendations.length >= 2 && <div className="vote-callout"><div><Users aria-hidden="true" /><span><strong>{chinese ? '跟朋友一起決定' : 'Deciding with friends?'}</strong><small>{chinese ? '用前 3 間開一個投票，傳連結就能投，不用登入。' : 'Start a vote on the top 3. Friends vote from a link, no sign-in.'}</small></span></div><button className="secondary-button" onClick={startVote} disabled={voteBusy}>{voteBusy ? (chinese ? '建立中…' : 'Starting…') : (chinese ? '揪團投票' : 'Start a vote')}</button></div>}<RecommendationList recommendations={recommendations} onSelectPlace={openPlace} onToggleFavorite={toggleFavorite} isFavorite={id => favorites.some(item => item.place_id === id)} showAll={showAll || Boolean(reviewCoverage)} onShowAll={() => setShowAll(true)} currentLanguage={currentLanguage} /></div><div className={`results-map ${viewMode === 'list' ? 'mobile-hidden' : ''}`}><MapComponent userLocation={location} recommendations={recommendations} onLocationSelect={openPlace} currentLanguage={currentLanguage} /></div></div>}
          </section>}
        </> : <section className="saved-section" aria-labelledby="saved-title"><div className="saved-heading"><div><p className="eyebrow">{favorites.length} {chinese ? '間餐廳' : 'places'}</p><h1 id="saved-title">{text.saved}</h1></div><button className="secondary-button" onClick={syncFavorites}><Cloud aria-hidden="true" />{text.sync}</button></div><div className="saved-discovery"><strong>{chinese ? '讓口袋名單變成今天這一餐' : 'Try a saved place today'}</strong><p>{chinese ? `以探索位置「${locationLabel || '尚未設定'}」查詢，步行 ${walkMinutes} 分鐘內；沿用每人預算，重新確認營業狀態。` : `Within ${walkMinutes} min of ${locationLabel || 'your search location'}, with your budget and refreshed hours.`}</p><button className="primary-button" disabled={isLoading || !favorites.length} onClick={exploreSaved}>{isLoading ? (chinese ? '確認營業狀態中…' : 'Checking hours…') : (chinese ? '附近有開的收藏' : 'Saved places open nearby')}</button></div><RecommendationList recommendations={savedRecommendations} onSelectPlace={openPlace} onToggleFavorite={toggleFavorite} isFavorite={id => favorites.some(item => item.place_id === id)} showAll emptyMessage={chinese ? '還沒有收藏餐廳' : 'No saved places yet'} currentLanguage={currentLanguage} /></section>}
      </main>

      <nav className="bottom-nav" aria-label={chinese ? '主要導覽' : 'Primary navigation'}><button className={activeTab === 'explore' ? 'active' : ''} aria-pressed={activeTab === 'explore'} onClick={() => { if (voteId) leaveVote(); setActiveTab('explore'); }}><Search aria-hidden="true" /><span>{text.explore}</span></button><button className={activeTab === 'saved' ? 'active' : ''} aria-pressed={activeTab === 'saved'} onClick={() => { if (voteId) leaveVote(); setActiveTab('saved'); }}><Bookmark aria-hidden="true" /><span>{text.saved}</span>{favorites.length > 0 && <em>{favorites.length}</em>}</button></nav>

      <PlaceDetailModal place={selectedPlace} isOpen={showDetail} onClose={() => setShowDetail(false)} onNavigate={navigate} onToggleFavorite={toggleFavorite} onShare={share} onRescue={rescue} rescueBusy={isLoading} isFavorite={id => favorites.some(item => item.place_id === id)} currentLanguage={currentLanguage} onMenuLoaded={(placeId, menuInfo) => setSelectedPlace(current => current?.place_id === placeId ? { ...current, menuInfo } : current)} />
      {!showAssistant && <button className="chatbot-launcher" onClick={() => setShowAssistant(true)} aria-label={chinese ? '開啟 AI 聊天，詢問目前畫面' : 'Open AI chat about this screen'}><MessageCircle aria-hidden="true" /><span>{chinese ? 'AI 聊天' : 'AI Chat'}</span></button>}
      <AIChatModal selectedPlace={showDetail ? selectedPlace : null} screen={{
        title: showDetail ? selectedPlace?.name : activeTab === 'saved' ? text.saved : searchStarted ? (resultTitle || text.top) : text.explore,
        page: showDetail ? 'place-details' : activeTab,
        search: activeTab === 'explore' ? searchQuery : '',
        locationReady: Boolean(location), loading: isLoading,
        reviewMode: Boolean(reviewCoverage),
        filters: activeTab === 'explore' && searchStarted ? { walkMinutes, budget: selectedPrice, openNow } : null,
        view: viewMode,
        visibleCount: activeTab === 'saved' ? favorites.length : (showAll || viewMode === 'map' ? recommendations.length : Math.min(3, recommendations.length))
      }} onPickForMe={pickForChat} pickReady={assistantPickPending && !isLoading} onPickHandled={() => setAssistantPickPending(false)} onSelectPlace={openPlace} isOpen={showAssistant} onClose={() => { setShowAssistant(false); setAssistantPickPending(false); }} recommendations={activeTab === 'saved' ? savedRecommendations : (showAll || viewMode === 'map' ? recommendations : recommendations.slice(0, 3))} currentLanguage={currentLanguage} />
      <LoginModal isOpen={showLogin} canClose onClose={() => setShowLogin(false)} onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }} />
      <RegisterModal isOpen={showRegister} canClose onClose={() => setShowRegister(false)} onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }} />
    </div>
  );
}

export default App;
